import React from 'react';
import { Typography, Box, Grid, Divider, Link } from '@mui/material';
import { formatDuration } from '../common/utils';

const MovieInfo = ({ movieData }) => {
  // URL regex pattern for detecting links
  const urlPattern = /(https?:\/\/[^\s]+)/g;

  // Function to render text with links
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // Split by URLs
    const parts = text.split(urlPattern);
    const matches = text.match(urlPattern) || [];
    
    // Reconstruct the text with links
    return parts.map((part, index) => {
      // Current part is regular text
      if (index % 2 === 0) {
        return part;
      }
      // Current part is a URL
      const url = matches[Math.floor(index / 2)];
      return (
        <Link 
          key={index} 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ 
            color: theme => theme.palette.primary.main,
            textDecoration: 'underline',
            wordBreak: 'break-word' 
          }}
        >
          {url}
        </Link>
      );
    });
  };

  // Function to render formatted text with preserved line breaks and links
  const renderFormattedText = (text) => {
    if (!text) return null;
    
    // Split the text by newline characters
    const paragraphs = text.split('\n');
    
    return paragraphs.map((paragraph, index) => {
      // Process links within each paragraph
      const processedText = renderTextWithLinks(paragraph);
      
      return (
        <Typography 
          key={index} 
          variant="body1" 
          paragraph={index < paragraphs.length - 1}
          sx={{ 
            whiteSpace: 'pre-line',
            mb: index < paragraphs.length - 1 ? 2 : 0
          }}
        >
          {processedText}
        </Typography>
      );
    });
  };

  // Format different types of values appropriately
  const formatValue = (key, value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    // Special handling for duration
    if (key === 'duration') {
      return formatDuration(value);
    }

    // Handle arrays of objects (like cast with name property)
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      
      if (typeof value[0] === 'object' && value[0] !== null) {
        return value.map(item => item.name || JSON.stringify(item)).join(', ');
      }
      return value.join(', ');
    }

    // Return the value as is
    return value;
  };

  // Function to convert camelCase to Title Case for display
  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1') // Insert a space before all capital letters
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
  };

  // Info Item component
  const InfoItem = ({ label, value }) => {
    if (value === null) return null;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {label.toUpperCase()}
        </Typography>
        <Typography variant="body1">
          {value}
        </Typography>
      </Box>
    );
  };

  const { info = {} } = movieData || {};
  const title = info.title || movieData?.title || 'Untitled';
  
  // Fields to exclude from general display
  const excludedFields = ['title', 'summary', 'description'];
  
  // Group remaining fields logically
  const metadataFields = Object.entries(info)
    .filter(([key]) => !excludedFields.includes(key))
    .map(([key, value]) => ({
      key,
      label: formatLabel(key),
      value: formatValue(key, value)
    }))
    .filter(item => item.value !== null);
  
  // Split into two roughly equal columns
  const midpoint = Math.ceil(metadataFields.length / 2);
  const leftColumnFields = metadataFields.slice(0, midpoint);
  const rightColumnFields = metadataFields.slice(midpoint);

  return (
    <Box sx={{ mt: 4, color: 'text.primary' }}>
      {/* Title */}
      <Typography variant="h4" gutterBottom>{title}</Typography>
      
      {/* Summary Section (if present) */}
      {info.summary && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Summary</Typography>
          {renderFormattedText(info.summary)}
          <Divider sx={{ my: 3 }} />
        </Box>
      )}
      
      {/* Description Section (if present) */}
      {info.description && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Description</Typography>
          {renderFormattedText(info.description)}
          <Divider sx={{ my: 3 }} />
        </Box>
      )}
      
      {/* Additional Info Section */}
      {metadataFields.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Details</Typography>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              {leftColumnFields.map(field => (
                <InfoItem 
                  key={field.key} 
                  label={field.label} 
                  value={field.value} 
                />
              ))}
            </Grid>
            
            {/* Right Column */}
            <Grid item xs={12} md={6}>
              {rightColumnFields.map(field => (
                <InfoItem 
                  key={field.key} 
                  label={field.label} 
                  value={field.value} 
                />
              ))}
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default MovieInfo;